/**
 * Organizzazione (decision of 03/10/2026, phase 1): who does what in K-Rev,
 * read-only for everybody with the module.
 *
 * - The company roles (ruoli aziendali) are data: a system table, edited by
 *   administrators and supervisors, with the names of the people. They are not
 *   the access profiles of the program.
 * - The responsibility matrix (RACI) and the rules are decisions of the CFO:
 *   they live here, cite roles only, never names, and change with a release.
 */
import { z } from 'zod';
import { entryCodeSchema } from './reference-tables.js';
import { idSchema, optionalText, requiredText } from './validation.js';
/** The system table of the company roles. */
export const COMPANY_ROLES_TABLE = 'ruoli-aziendali';
/** Date of the version of the matrix and of the rules shown in the app. */
export const ORGANIZATION_UPDATED = '2026-10-03';
// Updated the same day with the rule on the terms: the preventivo is internal,
// approved it becomes the offer (ADR-013).
/* --- Company roles ------------------------------------------------------------ */
/** Internal (employee, partner) or external (supplier, consultant). */
export const PERSON_KINDS = ['INT', 'EST'];
export const rolePersonSchema = z.object({
    name: requiredText(100),
    kind: z.enum(PERSON_KINDS, { message: 'Interno o esterno' }),
    userId: idSchema.nullish().transform((value) => value ?? null),
});
/**
 * The roles the rules rely on (allegato B): they are locked and held by users
 * of K-Management, since the program asks them to decide.
 */
export const RULE_ROLES = {
    ad: 'AD',
    cfo: 'CFO',
    operations: 'OPS',
    training: 'FORM',
    marketing: 'MKT',
};
/**
 * The people written in a cell of the table: «Nome (EST); Altro nome». Each
 * name may end with (INT) or (EST); without, the person is internal.
 */
export function parsePeople(text) {
    if (!text)
        return [];
    return text
        .split(/[;\n]/)
        .map((part) => part.trim())
        .filter(Boolean)
        .map((part) => {
        const match = /^(.*?)\s*\((INT|EST)\)$/i.exec(part);
        return match && match[1]
            ? { name: match[1].trim(), kind: match[2].toUpperCase() }
            : { name: part, kind: 'INT' };
    });
}
/** The people as a cell of the table holds them: the inverse of parsePeople. */
export function formatPeople(people) {
    const text = people.map((person) => `${person.name.trim()} (${person.kind})`).join('; ');
    return text || null;
}
const companyRoleFields = {
    title: requiredText(200),
    parent: z.preprocess((value) => (typeof value === 'string' && value.trim() === '' ? null : value), entryCodeSchema.nullish().transform((value) => value ?? null)),
    holders: z.array(rolePersonSchema).max(20, { message: 'Al massimo 20 persone' }).default([]),
    substitute: rolePersonSchema.nullish().transform((value) => value ?? null),
    notes: optionalText(300),
};
/** Body of `PUT /api/organization/roles/:code` and, with the code, of POST. */
export const companyRoleSchema = z.object(companyRoleFields);
export const createCompanyRoleSchema = z.object({
    code: z.preprocess((value) => (typeof value === 'string' && value.trim() === '' ? undefined : value), entryCodeSchema.optional()),
    ...companyRoleFields,
});
/**
 * The roles as a tree, in the order given: a role whose parent is missing is
 * at the top, and a loop in the parents never hides a role.
 */
export function roleTree(roles) {
    const codes = new Set(roles.map((role) => role.code));
    const placed = new Set();
    const build = (role) => {
        placed.add(role.code);
        return {
            role,
            children: roles
                .filter((child) => child.parent === role.code && !placed.has(child.code))
                .map(build),
        };
    };
    const tree = roles.filter((role) => !role.parent || !codes.has(role.parent)).map(build);
    // Roles caught in a loop of parents: shown at the top rather than lost.
    for (const role of roles)
        if (!placed.has(role.code))
            tree.push(build(role));
    return tree;
}
/** Whether giving `code` the parent `parent` would make a role report to itself. */
export function createsLoop(roles, code, parent) {
    const parents = new Map(roles.map((role) => [role.code, role.parent]));
    const seen = new Set();
    for (let current = parent; current; current = parents.get(current) ?? null) {
        if (current === code)
            return true;
        if (seen.has(current))
            return false;
        seen.add(current);
    }
    return false;
}
/* --- Responsibility matrix (allegato C, 03/10/2026) ---------------------------- */
export const RACI_LETTERS = ['R', 'A', 'C', 'I'];
export const RACI_LETTER_LABELS = {
    R: 'Esegue',
    A: 'Ne risponde',
    C: 'Consultato',
    I: 'Informato',
};
/** What each letter means in K-Management. */
export const RACI_LETTER_MEANINGS = {
    R: 'In K-Management è l’utente che può compiere l’azione o far avanzare lo stato.',
    A: 'Uno solo per attività. Riceve gli avvisi se l’attività resta ferma.',
    C: 'Non blocca, salvo dove indicato nella colonna «Blocchi».',
    I: 'Riceve una notifica quando l’attività è completata.',
};
/** The columns of the matrix: company roles and the roles held on each quote or job. */
export const RACI_ROLES = [
    { key: 'AD', label: 'AD' },
    { key: 'CFO', label: 'CFO' },
    { key: 'COMMERCIALE', label: 'Commerciale' },
    { key: 'PM', label: 'PM' },
    { key: 'ACCOUNT', label: 'Account' },
    { key: 'OPS', label: 'Resp. Operations' },
    { key: 'FORM', label: 'Resp. Formazione' },
    { key: 'MKT', label: 'Resp. Marketing' },
    { key: 'CONSULENTI', label: 'Consulenti esterni' },
];
function cell(letters, note, hypothesis = false) {
    return {
        letters: [...letters],
        ...(note ? { note } : {}),
        ...(hypothesis ? { hypothesis } : {}),
    };
}
const hypothesis = (letters) => cell(letters, undefined, true);
export const RACI_MATRIX = [
    {
        title: 'Prima dell’offerta',
        activities: [
            {
                number: '1',
                activity: 'Generazione contatti',
                cells: { MKT: cell('RA'), AD: cell('I') },
                inApp: false,
            },
            {
                number: '2',
                activity: 'Registrazione in Pipedrive',
                cells: { MKT: cell('A', 'esegue chi è abilitato a Pipedrive') },
                inApp: false,
            },
            {
                number: '3',
                activity: 'Assegnazione opportunità',
                cells: { MKT: cell('R'), AD: cell('A'), COMMERCIALE: cell('I') },
                inApp: false,
            },
        ],
    },
    {
        title: 'Offerta',
        activities: [
            {
                number: '4',
                activity: 'Preventivo: costi e ricavi',
                cells: { COMMERCIALE: cell('RA'), FORM: cell('C'), CFO: cell('I') },
                inApp: true,
            },
            {
                number: '5a',
                activity: 'Controlli sul preventivo: esito «autonomia»',
                cells: { COMMERCIALE: cell('RA') },
                inApp: true,
            },
            {
                number: '5b',
                activity: 'Controlli sul preventivo: esito «CFO informato»',
                cells: { COMMERCIALE: cell('RA'), CFO: cell('I') },
                inApp: true,
            },
            {
                number: '5c',
                activity: 'Controlli sul preventivo: esito «convalida del CFO»',
                cells: { CFO: cell('RA', 'sostituto: AD'), COMMERCIALE: cell('I') },
                inApp: true,
                block: 'Senza convalida il preventivo non diventa offerta',
            },
            {
                number: '6a',
                activity: 'Approvazione del preventivo sotto 50.000 €: diventa offerta',
                cells: { COMMERCIALE: cell('RA'), CFO: cell('I') },
                inApp: true,
            },
            {
                number: '6b',
                activity: 'Approvazione del preventivo da 50.000 €, anche in una nuova versione',
                cells: { AD: cell('RA', 'sostituto: CFO'), CFO: cell('I'), COMMERCIALE: cell('I') },
                inApp: true,
                block: 'Senza l’approvazione dell’AD il preventivo non diventa offerta',
            },
            {
                number: '7',
                activity: 'Invio offerta e trattativa',
                cells: { COMMERCIALE: cell('RA'), AD: hypothesis('I') },
                inApp: true,
                block: 'Ogni modifica è una nuova versione: controlli e approvazioni si ripetono',
            },
        ],
    },
    {
        title: 'Contratto e commessa',
        activities: [
            {
                number: '8',
                activity: 'Firma contratto',
                cells: {
                    AD: cell('RA', 'sostituto: CFO, con delega'),
                    CONSULENTI: cell('C', 'se il contratto è complesso'),
                    COMMERCIALE: cell('I'),
                    CFO: cell('I'),
                },
                inApp: true,
                block: 'Un contratto complesso non passa a «firmato» senza parere registrato',
            },
            {
                number: '9',
                activity: 'Archiviazione contratto',
                cells: { COMMERCIALE: cell('R'), CFO: cell('A') },
                inApp: true,
            },
            {
                number: '10',
                activity: 'Offerta accettata → commessa, nomina di PM e Account',
                cells: {
                    CFO: cell('RA', 'per ora; poi chi avrà la modifica delle commesse'),
                    PM: cell('I'),
                    ACCOUNT: cell('I'),
                    COMMERCIALE: cell('I'),
                    OPS: cell('I'),
                },
                inApp: true,
                block: 'L’offerta accettata va in una commessa del suo cliente; la commessa va «in corso» solo con PM, Account e date',
            },
            {
                number: '11',
                activity: 'Erogazione',
                cells: {
                    PM: cell('RA'),
                    FORM: hypothesis('C'),
                    OPS: hypothesis('C'),
                    AD: hypothesis('I'),
                },
                inApp: true,
            },
        ],
    },
    {
        title: 'Fatturazione e incasso',
        activities: [
            {
                number: '12',
                activity: 'Via libera alla fatturazione',
                cells: {
                    CFO: cell('RA', 'per ora, allineato con il PM'),
                    PM: cell('C'),
                    ACCOUNT: cell('C'),
                },
                inApp: true,
                block: 'Avviso a PM e CFO dopo 7 giorni senza via libera',
            },
            {
                number: '13',
                activity: 'Emissione fattura',
                cells: { CFO: cell('RA'), PM: cell('I'), COMMERCIALE: cell('I') },
                inApp: true,
            },
            {
                number: '14',
                activity: 'Incasso',
                cells: { CFO: cell('RA'), PM: cell('I'), AD: cell('I') },
                inApp: true,
            },
            {
                number: '15',
                activity: 'Scaduto dall’8° giorno: allineamento e contatto informale',
                cells: {
                    PM: cell('R', 'o il commerciale, come deciso'),
                    COMMERCIALE: cell('R', 'o il PM, come deciso'),
                    CFO: cell('A'),
                },
                inApp: true,
                block: 'Solo avviso interno, nessun messaggio al cliente',
            },
            {
                number: '16',
                activity: 'Scaduto dal 21° giorno: comunicazione formale',
                cells: { CFO: cell('RA'), PM: cell('C'), COMMERCIALE: cell('C'), AD: cell('I') },
                inApp: true,
                block: 'Solo avviso interno, nessun messaggio al cliente',
            },
            {
                number: '17',
                activity: 'Scaduto dal 61° giorno: valutazione legale',
                cells: {
                    CFO: cell('R'),
                    AD: hypothesis('A'),
                    CONSULENTI: cell('C', 'legale esterno'),
                    PM: cell('I'),
                },
                inApp: true,
                block: 'Solo avviso interno, nessun messaggio al cliente',
            },
        ],
    },
    {
        title: 'Fornitori',
        activities: [
            {
                number: '18a',
                activity: 'Controllo fatture fornitori legate a una commessa',
                cells: { CFO: cell('RA', 'per ora, sentito il PM'), PM: cell('C') },
                inApp: true,
                block: 'Nessun pagamento senza controllo registrato',
            },
            {
                number: '18b',
                activity: 'Controllo fatture fornitori non legate a una commessa',
                cells: { CFO: cell('RA') },
                inApp: true,
                block: 'Nessun pagamento senza controllo registrato',
            },
            {
                number: '19',
                activity: 'Pagamento fornitori',
                cells: { CFO: cell('RA'), PM: cell('I') },
                inApp: true,
                block: 'Pagamenti bloccati dopo un cambio IBAN, fino alla telefonata di verifica',
            },
        ],
    },
    {
        title: 'Chiusura contabile',
        activities: [
            {
                number: '20',
                activity: 'Chiusura dell’anno della Tesoreria',
                cells: { CFO: cell('RA'), AD: cell('I') },
                inApp: true,
                block: 'Si chiude solo con i controlli verdi o le differenze spiegate; riaprire è solo dell’amministratore, con il motivo',
            },
        ],
    },
];
const rule = (text, tag) => (tag ? { text, tag } : { text });
const topic = (text, children) => ({ text, children });
export const OPEN_POINTS = [
    'Criterio definitivo di assegnazione delle opportunità',
    'Ingresso dell’SDR esterno: accessi e passaggio dei contatti qualificati',
    'Ingresso dei nuovi commerciali e della contabile; ripartizione dei compiti con il CFO',
    'Permessi in K-Management del Responsabile Marketing',
    'Fatturazione della formazione finanziata',
    'Listino di licenze e sviluppo: valori e chi lo aggiorna',
    'Costi standard a giornata delle figure interne',
    'Sostituto IT senza accesso a K-Management',
    'Attivazione dell’integrazione con K-Presenze',
    'Commesse: chi, oltre al CFO, potrà modificarle e che cosa vedrà e farà il commerciale abilitato',
    'Costo delle persone sulle commesse (discussione dedicata)',
    'Licenze e sviluppo senza prezzo di listino: come valutarne lo sconto',
    'Ipotesi della matrice: AD informato all’invio (7), consultazioni in erogazione (11), AD responsabile della valutazione legale (17)',
];
export const ORGANIZATION_RULES = [
    topic('Ruoli e sostituzioni', [
        rule('Ogni offerta ha **un solo commerciale**: chi segue l’opportunità e prepara l’offerta, qualunque sia la sua area.'),
        topic('Ogni commessa ha **un PM e un Account**, più il commerciale che l’ha venduta', [
            rule('Oggi una persona per figura; più avanti anche più di una.'),
            rule('L’Account gestisce il cliente e lo accompagna fino al termine della commessa.'),
            rule('PM e Account vedono tutta la commessa, compresi lo storico e il budget.'),
            rule('Il commerciale vede la commessa solo se abilitato, commessa per commessa.'),
            rule('Si scelgono solo tra gli utenti di K-Management; il PM può essere il commerciale stesso.'),
            rule('Le figure non modificano la commessa: crea, modifica e chiude chi ha il permesso del modulo Commesse e progetti (per ora il CFO), dopo l’allineamento con loro.'),
        ]),
        topic('Assenze e sostituti', [
            rule('Chi è assente imposta il periodo (dal… al…): le richieste vanno al sostituto indicato in Ruoli.'),
            rule('Non esistono passaggi automatici al sostituto per tempo scaduto.'),
            rule('Ogni atto compiuto in sostituzione resta registrato come tale.'),
        ]),
        rule('Il sostituto del CFO (l’AD) fa **solo le convalide**: emissione fatture e pagamenti attendono il rientro del CFO.'),
        rule('Nessuno convalida un’offerta di cui è il commerciale; se il sostituto è il commerciale, la convalida attende il titolare.'),
        rule('I consulenti esterni non accedono: il loro parere lo registra l’AD o il CFO.'),
    ]),
    topic('Preventivo e offerta', [
        rule('Il **preventivo** è per uso interno: si costruisce valutando costi e ricavi e non ha numero.'),
        rule('Approvato, diventa **offerta**, il documento per il cliente: numero OFF-AAAA-NNNN con la sua versione.'),
        rule('L’offerta non si modifica: ogni cambiamento chiesto dal cliente è una **nuova versione**, che riparte dal preventivo.'),
        rule('Validità dell’offerta [[30 giorni]], modificabile sulla singola offerta.'),
        topic('Controlli automatici sul preventivo', [
            {
                text: '**Formazione, coaching, consulenza e altre voci: margine**',
                bands: [
                    { tone: 'red', label: 'sotto [[30%]] convalida del CFO', weight: 30 },
                    { tone: 'yellow', label: 'da 30% a 40% CFO informato', weight: 25 },
                    { tone: 'green', label: 'da [[40%]] autonomia', weight: 30 },
                ],
            },
            rule('Margine = (prezzo − costo) ÷ prezzo: è un margine sul prezzo, non un ricarico sul costo.'),
            rule('Il costo comprende i compensi di formatori e coach esterni e, per il personale interno, il costo standard a giornata. Le trasferte non entrano nel calcolo.'),
            rule('Prezzo minimo = costo ÷ (1 − margine desiderato). Esempio: costo 610 €, per il 40% = 610 ÷ 0,60 = 1.016,67 €.'),
            {
                text: '**Licenze e sviluppo: sconto sul listino**',
                bands: [
                    { tone: 'green', label: 'a listino: autonomia', weight: 25 },
                    { tone: 'yellow', label: 'fino al [[10%]] CFO informato', weight: 30 },
                    { tone: 'red', label: 'oltre: convalida del CFO', weight: 30 },
                ],
            },
            rule('**Offerte miste**: ogni voce con la sua regola; all’offerta si applica l’esito più restrittivo.'),
            rule('**Tempi di incasso**: standard [[ricevimento fattura]]; qualsiasi termine diverso richiede la convalida del CFO, a qualsiasi importo.', 'block'),
        ]),
        topic('Approvazione', [
            rule('Sotto [[50.000 €]] approva il commerciale; da 50.000 € in su l’AD, in sua assenza il CFO. Vale anche per ogni nuova versione.', 'block'),
            rule('Approvazione per importo e convalide sono indipendenti: possono servire entrambe.'),
        ]),
        topic('Invio e trattativa', [
            rule('Il preventivo diventa offerta solo quando tutte le approvazioni e convalide richieste sono registrate.', 'block'),
            rule('A ogni modifica di prezzo, costo o tempi di incasso si crea una nuova versione: i controlli si rieseguono sui nuovi valori e servono di nuovo le convalide e l’approvazione.'),
        ]),
    ]),
    topic('Contratto', [
        rule('Firma l’AD; in sua assenza il CFO, con delega. I commerciali non firmano.'),
        topic('Contratto complesso', [
            rule('Penali o clausole non standard (indicato sull’offerta dal commerciale).'),
            rule('Durata oltre [[12 mesi]].'),
            rule('Cliente con [[1.000]] dipendenti o più, solo per il primo contratto: sul cliente resta il riferimento al contratto validato (data e consulente).'),
            rule('Non passa a «firmato» senza il parere registrato: consulente, data, esito, chi lo ha registrato. In fase di offerta compare solo un avviso.', 'block'),
            rule('Il consulente da coinvolgere lo decide l’AD, in sua assenza il CFO.'),
        ]),
        rule('Accettata, l’offerta entra in una commessa del suo cliente, nuova o già aperta: la collega chi ha la modifica del modulo Commesse e progetti.'),
        rule('Il contratto firmato lo archivia il commerciale, in K-Management e su SharePoint: Clienti › [Cliente] › Contratti › Contratti firmati › [Anno].'),
    ]),
    topic('Fatturazione', [
        rule('Alla nascita della commessa si crea il piano di fatturazione con le rate previste dall’offerta; per ora lo conferma il CFO, allineato con il PM.'),
        rule('Rate legate all’erogazione: per ora il CFO, allineato con il PM, segna la rata «fatturabile» ed emette la fattura.'),
        rule('Rate a data fissa (anticipo alla firma, canoni): arrivano al CFO alla scadenza, senza passare dal PM.'),
        rule('Se dopo [[7 giorni]] manca il via libera parte un avviso a PM e CFO.'),
        rule('Le fatture le emette il CFO.'),
    ]),
    topic('Scaduto e solleciti', [
        rule('K-Management **non invia mai messaggi al cliente**: gli avvisi sono solo interni, insieme a PM, commerciale e CFO.'),
        rule('I giorni di ritardo si contano dalla scadenza della fattura.'),
        rule('Prima di qualsiasi contatto i tre si allineano e decidono chi contatta il cliente.'),
        rule('Dall’[[8°]] giorno: contatto informale, del PM o del commerciale.'),
        rule('Dal [[21°]] giorno: comunicazione formale del CFO, con PM e commerciale.'),
        rule('Dal [[61°]] giorno: valutazione dell’intervento legale, CFO con l’AD.'),
        rule('Ogni contatto con il cliente si annota sulla commessa: chi, quando, risposta, prossimo passo.'),
    ]),
    topic('Fornitori e pagamenti', [
        rule('Fatture legate a una commessa: per ora le controlla il CFO, sentito il PM (importo e prestazione).'),
        rule('Fatture non legate a una commessa: le controlla il CFO.'),
        rule('Paga il CFO, solo fatture con controllo registrato.', 'block'),
        topic('Cambio IBAN', [
            rule('L’IBAN sta nell’anagrafica del fornitore e lo modifica solo il CFO.'),
            rule('Ogni modifica blocca i pagamenti a quel fornitore finché non è registrata la telefonata di verifica, a un numero già noto.', 'block'),
            rule('Se una fattura riporta un IBAN diverso da quello in anagrafica, viene segnalato prima del pagamento.'),
        ]),
    ]),
    topic('Riservatezza dei costi', [
        rule('Il **costo standard** a giornata delle figure interne lo definisce il CFO ed è visibile a chi prepara l’offerta.'),
        rule('Il **costo aziendale reale** è riservato a CFO e AD: PM e commerciali vedono le ore, non gli importi.'),
        rule('Il margine d’offerta e il margine a consuntivo sono due calcoli distinti.'),
        rule('Tutti gli importi sono IVA esclusa.'),
    ]),
    topic('Integrazioni', [
        rule('Pipedrive: contatti e trattative prima dell’offerta; nessuna funzione in K-Management.'),
        rule('SharePoint: archivio dei contratti firmati.'),
        rule('K-Presenze: K-Management leggerà le ore rendicontate per budget e consuntivo delle commesse, senza modificarle.', 'open'),
    ]),
    topic('Soglie in vigore', [
        rule('Approvazione dell’AD da [[50.000 €]]'),
        rule('Margine per l’autonomia [[40%]] · senza convalida [[30%]]'),
        rule('Sconto su listino senza convalida [[10%]]'),
        rule('Termine di pagamento standard [[ricevimento fattura]]'),
        rule('Contratto complesso oltre [[12 mesi]] o con cliente da [[1.000]] dipendenti'),
        rule('Scaduto [[8]] · [[21]] · [[61]] giorni'),
        rule('Avviso per via libera mancante dopo [[7 giorni]]'),
        rule('Validità delle offerte [[30 giorni]]'),
        rule('I valori in vigore si cambiano in Tabelle: Parametri, Soglie di margine e Soglie licenze.'),
    ]),
    topic('Punti aperti', OPEN_POINTS.map((point) => rule(point, 'open'))),
];
/** Splits the text of a rule into its plain, bold and threshold parts. */
export function ruleTextParts(text) {
    const parts = [];
    const pattern = /\*\*(.+?)\*\*|\[\[(.+?)\]\]/g;
    let last = 0;
    for (const match of text.matchAll(pattern)) {
        if (match.index > last)
            parts.push({ kind: 'text', text: text.slice(last, match.index) });
        parts.push(match[1] !== undefined
            ? { kind: 'bold', text: match[1] }
            : { kind: 'value', text: match[2] });
        last = match.index + match[0].length;
    }
    if (last < text.length)
        parts.push({ kind: 'text', text: text.slice(last) });
    return parts;
}
/** The text of a rule without marks: for the search. */
export function plainRuleText(node) {
    return [node.text, ...(node.bands ?? []).map((band) => band.label)]
        .join(' ')
        .replace(/\*\*|\[\[|\]\]/g, '');
}
/** How many rules (leaves) a topic holds. */
export function ruleCount(node) {
    return node.children ? node.children.reduce((sum, child) => sum + ruleCount(child), 0) : 1;
}
//# sourceMappingURL=organization.js.map